import { movieGenres, movieRegions, movieSorts, movieYears } from "@/mocks/site";

interface MovieFiltersProps {
  genre: string;
  region: string;
  year: string;
  sort: string;
  resultCount: number;
  onGenreChange: (value: string) => void;
  onRegionChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onReset: () => void;
}

export default function MovieFilters({
  genre,
  region,
  year,
  sort,
  resultCount,
  onGenreChange,
  onRegionChange,
  onYearChange,
  onSortChange,
  onReset,
}: MovieFiltersProps) {
  const hasFilter = genre !== "全部" || region !== "全部地区" || year !== "全部年份";

  return (
    <section className="rounded-lg border border-background-800 bg-background-900/70 p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="w-12 shrink-0 text-xs text-foreground-500">类型</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {movieGenres.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onGenreChange(item)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                genre === item
                  ? "bg-accent-500 font-medium text-background-950"
                  : "bg-background-800/80 text-foreground-300 hover:bg-background-700 hover:text-foreground-50"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-background-800/70 pt-4">
        <span className="w-12 shrink-0 text-xs text-foreground-500">地区</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {movieRegions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onRegionChange(item)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                region === item
                  ? "bg-primary-500 text-foreground-50"
                  : "bg-background-800/80 text-foreground-300 hover:bg-background-700 hover:text-foreground-50"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 border-t border-background-800/70 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex items-center gap-3">
          <span className="w-12 shrink-0 text-xs text-foreground-500">年份</span>
          <div className="relative">
            <select
              value={year}
              onChange={(event) => onYearChange(event.target.value)}
              aria-label="按年份筛选"
              className="h-9 appearance-none rounded-md border border-background-700 bg-background-950 pl-3 pr-8 text-xs text-foreground-100 outline-none transition-colors focus:border-accent-500"
            >
              {movieYears.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
          </div>
        </div>

        <div className="flex items-center gap-3 sm:ml-auto">
          <span className="text-xs text-foreground-500">排序</span>
          <div className="relative">
            <select
              value={sort}
              onChange={(event) => onSortChange(event.target.value)}
              aria-label="排序方式"
              className="h-9 appearance-none rounded-md border border-background-700 bg-background-950 pl-3 pr-8 text-xs text-foreground-100 outline-none transition-colors focus:border-accent-500"
            >
              {movieSorts.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-800/70 pt-4">
        <p className="text-xs text-foreground-500">
          共找到 <span className="text-accent-400">{resultCount}</span> 部符合条件的影片
        </p>
        <button
          type="button"
          onClick={onReset}
          disabled={!hasFilter}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
            hasFilter
              ? "text-foreground-300 hover:bg-background-800 hover:text-accent-400"
              : "cursor-not-allowed text-foreground-600"
          }`}
        >
          <i className="ri-refresh-line text-sm" />
          重置筛选
        </button>
      </div>
    </section>
  );
}